import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import { workspaceService, websiteService } from '../../lib/resellerService';

const STATUS_COLORS = {
  active: 'bg-green-500/10 text-green-400',
  draft: 'bg-amber-500/10 text-amber-400',
  inactive: 'bg-red-500/10 text-red-400',
};

const ResellerWebsites = () => {
  const { userProfile } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [formData, setFormData] = useState({ name: '', domain: '', status: 'active', description: '' });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: ws, error: wsError } = await workspaceService?.getMyWorkspace();
      if (wsError) throw wsError;
      setWorkspace(ws);
      if (ws?.id) {
        const { data: sites, error: sitesError } = await websiteService?.getByWorkspace(ws?.id);
        if (sitesError) throw sitesError;
        setWebsites(sites || []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load websites');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingWebsite(null);
    setFormData({ name: '', domain: '', status: 'active', description: '' });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (website) => {
    setEditingWebsite(website);
    setFormData({ name: website?.name || '', domain: website?.domain || '', status: website?.status || 'active', description: website?.description || '' });
    setFormError(null);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!formData?.name?.trim()) { setFormError('Website name is required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (editingWebsite) {
        const { error } = await websiteService?.update(editingWebsite?.id, formData);
        if (error) throw error;
      } else {
        const { error } = await websiteService?.create(workspace?.id, formData);
        if (error) throw error;
      }
      setShowForm(false);
      await loadData();
    } catch (err) {
      setFormError(err?.message || 'Failed to save website');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this website?')) return;
    setDeletingId(id);
    try {
      const { error } = await websiteService?.delete(id);
      if (error) throw error;
      setWebsites(prev => prev?.filter(w => w?.id !== id));
    } catch (err) {
      alert(err?.message || 'Failed to delete website');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={setIsSidebarCollapsed} />
      <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header userName={userProfile?.name || userProfile?.email || ''} userRole={userProfile?.role || ''} />
        <main className="content-area">
          <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Icon name="Globe" size={20} className="text-blue-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Websites</h1>
                  <p className="text-sm text-muted-foreground">{workspace?.name || 'Your workspace'}</p>
                </div>
              </div>
              {workspace && (
                <button
                  onClick={openCreate}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Icon name="Plus" size={16} />
                  Add Website
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Icon name="Loader2" size={32} className="text-primary animate-spin" />
              </div>
            ) : error ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6 text-center">
                <Icon name="AlertCircle" size={24} className="text-destructive mx-auto mb-2" />
                <p className="text-destructive text-sm">{error}</p>
                <button onClick={loadData} className="mt-3 text-sm text-primary hover:underline">Try again</button>
              </div>
            ) : !workspace ? (
              <div className="bg-card border border-border rounded-xl p-10 text-center">
                <Icon name="Building2" size={40} className="text-muted-foreground mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-foreground mb-1">No Workspace Found</h3>
                <p className="text-sm text-muted-foreground">Contact your Super Admin to set up your reseller workspace.</p>
              </div>
            ) : websites?.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-10 text-center">
                <Icon name="Globe" size={40} className="text-muted-foreground mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-foreground mb-1">No Websites Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">Add your first website to get started.</p>
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                  <Icon name="Plus" size={16} />
                  Add Website
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {websites?.map((website) => (
                  <div key={website?.id} className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Globe" size={18} className="text-blue-400" />
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS?.[website?.status] || STATUS_COLORS?.active}`}>
                        {website?.status}
                      </span>
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{website?.name}</h3>
                    {website?.domain && (
                      <p className="text-xs text-primary mb-1">{website?.domain}</p>
                    )}
                    {website?.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{website?.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mb-4">
                      Created {new Date(website?.createdAt)?.toLocaleDateString()}
                    </p>
                    <div className="flex items-center gap-2 pt-3 border-t border-border">
                      <button
                        onClick={() => openEdit(website)}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Icon name="Pencil" size={13} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(website?.id)}
                        disabled={deletingId === website?.id}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors ml-auto"
                      >
                        {deletingId === website?.id ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Trash2" size={13} />}
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">
                {editingWebsite ? 'Edit Website' : 'Add Website'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {formError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Website Name *</label>
                <input
                  type="text"
                  value={formData?.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="My Client Website"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Domain</label>
                <input
                  type="text"
                  value={formData?.domain}
                  onChange={e => setFormData(prev => ({ ...prev, domain: e?.target?.value }))}
                  placeholder="www.example.com"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Status</label>
                <select
                  value={formData?.status}
                  onChange={e => setFormData(prev => ({ ...prev, status: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
                <textarea
                  value={formData?.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder="Brief description of this website..."
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {saving && <Icon name="Loader2" size={14} className="animate-spin" />}
                  {editingWebsite ? 'Save Changes' : 'Add Website'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerWebsites;
